/**
 * useScrollSpy — observes a list of section ids and returns the id of the
 * one currently most in view. Used by the top nav to highlight the active
 * section as the user scrolls.
 *
 * WHY IntersectionObserver over scroll-position math: the browser already
 * does the geometry; we just listen. No rAF loop, no scroll-event throttling.
 */
import { useEffect, useState } from "react";

export function useScrollSpy(ids: string[], rootMargin = "-40% 0px -55% 0px"): string {
  const [active, setActive] = useState<string>(ids[0] ?? "");

  useEffect(() => {
    if (!ids.length) return;

    const elements = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);

    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry that's furthest "in" the viewport band defined by
        // rootMargin. Multiple sections can be intersecting simultaneously
        // during scroll — choose the one with the highest intersection ratio.
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        const top = visible.reduce((best, e) =>
          e.intersectionRatio > best.intersectionRatio ? e : best,
        );
        setActive(top.target.id);
      },
      // The negative top + bottom margins create a horizontal band roughly
      // mid-screen. A section "becomes active" only when it crosses into
      // that band, which feels more deliberate than firing on first pixel.
      { rootMargin, threshold: [0, 0.25, 0.5, 0.75, 1] },
    );

    for (const el of elements) observer.observe(el);
    return () => observer.disconnect();
  }, [ids.join("|"), rootMargin]);

  return active;
}
